// Which LPs a picker lists: running ones only (lib/lpStatus). Read once on
// mount and again when the tab comes back into view (no polling).
//
// isListed(lp_id, keep?) — keep: ids that stay listed whatever their state,
// e.g. the value a select currently holds, so an existing rule or ticket on a
// stopped LP still shows its LP. Until both lists have loaded, or if either
// fails, every LP is listed (nothing is hidden on a guess).

import { useCallback, useEffect, useState } from 'react';
import { lpAdminApi } from '@/services/api';
import { isLpRunning, lpStatusBucket } from '@/lib/lpStatus';

export function useListedLps(): (lpId: string, keep?: string | (string | null | undefined)[] | null) => boolean {
  const [running, setRunning] = useState<Set<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      Promise.all([lpAdminApi.list(), lpAdminApi.health()])
        .then(([list, health]) => {
          if (cancelled) return;
          const state = new Map((health.lps ?? []).map((h) => [h.lp_id, h.state as string]));
          setRunning(new Set((list.lps ?? [])
            .filter((lp) => isLpRunning(lpStatusBucket(lp.enabled, state.get(lp.lp_id))))
            .map((lp) => lp.lp_id)));
        })
        .catch(() => { if (!cancelled) setRunning(null); });
    };
    load();
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVis); };
  }, []);

  return useCallback((lpId, keep) => {
    if (!running) return true;
    if (running.has(lpId)) return true;
    const k = Array.isArray(keep) ? keep : [keep];
    return k.includes(lpId);
  }, [running]);
}
